import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import api from '../api/client';
import './KYC.css';

const STATUS_TEXT = {
  not_submitted: 'Not submitted. Complete all four steps below.',
  pending: 'Submitted. Our team is reviewing your details.',
  approved: 'Approved.',
  rejected: 'Rejected. Please correct the details below and resubmit.',
};

const errorText = (e) => e.response?.data?.detail || 'Something went wrong. Please try again.';

const Step = ({ title, done, children }) => (
  <section style={{ borderTop: '1px solid #ddd', padding: '12px 0' }}>
    <h3 style={{ margin: 0 }}>{done ? '✓ ' : ''}{title}</h3>
    {children}
  </section>
);

const KYC = () => {
  const [kyc, setKyc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pan, setPan] = useState('');
  const [account, setAccount] = useState('');
  const [ifsc, setIfsc] = useState('');
  const [residency, setResidency] = useState('resident');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      setKyc((await api.get('/api/kyc/my/')).data);
    } catch (e) {
      setError('Failed to load KYC status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (name, fn, okMsg) => {
    setBusy(name);
    try {
      const res = await fn();
      if (res?.data?.steps) setKyc(res.data);
      toast.success(okMsg);
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      setBusy('');
    }
  };

  const startAadhaar = async () => {
    setBusy('aadhaar');
    try {
      const res = await api.post('/api/kyc/aadhaar/start/', {});
      window.location.assign(res.data.redirect_url);
    } catch (e) {
      toast.error(errorText(e));
      setBusy('');
    }
  };

  if (loading) return <div className="container"><div className="content"><p className="loading">Loading...</p></div></div>;
  if (error) return <div className="container"><div className="content"><div className="error">{error}</div></div></div>;

  const s = kyc.steps;
  return (
    <div className="container">
      <div className="content">
        <h2 className="title">KYC</h2>
        <p className="status" role="status">Status: <strong>{kyc.status.replace('_', ' ')}</strong>. {STATUS_TEXT[kyc.status]}</p>
        {kyc.status === 'rejected' && kyc.reject_reason && <p role="alert">Reason: {kyc.reject_reason}</p>}

        <Step title="1. PAN" done={s.pan}>
          {s.pan && <p>Verified: {kyc.pan_masked}</p>}
          <form onSubmit={(e) => { e.preventDefault(); run('pan', () => api.post('/api/kyc/pan/', { pan }), 'PAN verified'); }}>
            <label>PAN <input value={pan} onChange={(e) => setPan(e.target.value.toUpperCase())} maxLength={10} className="input" placeholder="ABCDE1234F" /></label>
            <button type="submit" disabled={busy === 'pan' || pan.length !== 10}>{s.pan ? 'Update PAN' : 'Verify PAN'}</button>
          </form>
        </Step>

        <Step title="2. Aadhaar (via DigiLocker)" done={s.aadhaar}>
          <p>You will be sent to DigiLocker to consent. We store only the last 4 digits of your Aadhaar number.</p>
          {s.aadhaar && <p>Verified: XXXX XXXX {kyc.aadhaar_ref}</p>}
          <button onClick={startAadhaar} disabled={busy === 'aadhaar'}>{s.aadhaar ? 'Verify again' : 'Verify with DigiLocker'}</button>
        </Step>

        <Step title="3. Bank account" done={s.bank === 'verified'}>
          {s.bank === 'verified' && <p>Verified: account ending {kyc.bank_account_last4} ({kyc.bank_ifsc})</p>}
          {s.bank === 'pending' && <p>Awaiting confirmation from your bank (account ending {kyc.bank_account_last4}).</p>}
          <p>We send ₹1 to check the account is yours.</p>
          <form onSubmit={(e) => { e.preventDefault(); run('bank', () => api.post('/api/kyc/bank/', { account_number: account, ifsc }), 'Bank account submitted'); }}>
            <label>Account number <input value={account} onChange={(e) => setAccount(e.target.value)} inputMode="numeric" className="input" autoComplete="off" /></label>
            <label>IFSC <input value={ifsc} onChange={(e) => setIfsc(e.target.value.toUpperCase())} maxLength={11} className="input" /></label>
            <button type="submit" disabled={busy === 'bank' || !account || ifsc.length !== 11}>Verify bank account</button>
          </form>
        </Step>

        <Step title="4. Residency declaration" done={s.residency}>
          <form onSubmit={(e) => { e.preventDefault(); run('residency', () => api.post('/api/kyc/residency/', { residency }), 'Residency recorded'); }}>
            <label>I am a{' '}
              <select value={residency} onChange={(e) => setResidency(e.target.value)} className="select">
                <option value="resident">Resident Indian</option>
                <option value="nri">Non-Resident Indian</option>
                <option value="entity">Entity registered in India</option>
                <option value="foreign">Foreign national / entity</option>
              </select>
            </label>
            {residency !== 'resident' && <p role="note">Only resident Indian individuals can invest at this time.</p>}
            <button type="submit" disabled={busy === 'residency'}>Save declaration</button>
          </form>
        </Step>
      </div>
    </div>
  );
};

export default KYC;
